/**
 * Service Mutations Hook
 * Handles creating, updating, and deleting renovation services
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

interface CreateServiceInput {
  name: string;
  description?: string;
  icon?: string;
}

interface UpdateServiceInput {
  id: number;
  name: string;
  description?: string;
  icon?: string;
}

export const useServiceMutations = () => {
  const queryClient = useQueryClient();

  const createServiceMutation = useMutation({
    mutationFn: async (input: CreateServiceInput) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/services`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: input.name,
          description: input.description || "",
          icon: input.icon || "home",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create service");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["renovation-services"] });
      toast.success("Service created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create service: ${error.message}`);
    },
  });

  const updateServiceMutation = useMutation({
    mutationFn: async (input: UpdateServiceInput) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/services/${input.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: input.name,
          description: input.description || "",
          icon: input.icon || "home",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update service");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["renovation-services"] });
      toast.success("Service updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update service: ${error.message}`);
    },
  });

  const deleteServiceMutation = useMutation({
    mutationFn: async (serviceId: number) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/services/${serviceId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete service");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["renovation-services"] });
      toast.success("Service deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete service: ${error.message}`);
    },
  });

  return {
    createServiceMutation,
    updateServiceMutation,
    deleteServiceMutation,
    isCreating: createServiceMutation.isPending,
    isUpdating: updateServiceMutation.isPending,
    isDeleting: deleteServiceMutation.isPending,
  };
};
